import {
  ApiContextProvider,
  AuthApiContextProvider,
  AuthenticationProvider,
  DialogProvider,
  MessageProvider,
  TOKENS,
  useAuth,
  useAuthApi,
} from 'app/context';
import { ReactElement, useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { BaseLayout, ProtectedRoute } from 'app/core';
import { BreadcrumbStep, PageWrapper } from 'app/shared';
import { ROUTES } from 'app/utils';
import {
  ChangePassword,
  DoctorCreate,
  DoctorDetail,
  DoctorList,
  ForgotPassword,
  Login,
  MedicalCenterCreate,
  MedicalCenterDetail,
  MedicalCenterList,
  PatientCreate,
  PatientDetail,
  PatientList,
  PdfConfiguration,
  Profile,
  Register,
  ReportCreate,
  ReportDetail,
  ReportList,
  ResetPassword,
} from 'app/features';
import { useTranslation } from 'react-i18next';
import { getCookie } from 'react-use-cookie';

const RequireAuth = () => {
  const { access_token, setLoggedUser } = useAuth();

  const { authApi } = useAuthApi();

  useEffect(() => {
    if (access_token) {
      authApi.getCurrentAuthUserDetails().then((resp) => {
        setLoggedUser(resp.data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access_token]);

  let accessTkn = '';

  try {
    accessTkn = JSON.parse(getCookie(TOKENS)).access_token;
  } catch (error) {
    console.log('Token not found');
  }

  if (access_token || accessTkn) {
    return <Outlet />;
  } else {
    return <Navigate to={ROUTES.login} />;
  }
};

// const EnumLoader = () => {
//   const { utilsApi } = useApi();
//   useEffect(() => {
//     setEnums(utilsApi); //importare da app/utils
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);
//   return <Outlet />;
// };

export const AppRouter = (): ReactElement => {
  const { t } = useTranslation();

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path={ROUTES.home}>
        <Route index element={<Navigate to={ROUTES.me} />} />
        <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
        <Route path={ROUTES.resetPassword} element={<ResetPassword />} />
        <Route path={ROUTES.login} element={<Login />} />
        <Route path={ROUTES.register} element={<Register />} />
        <Route element={<RequireAuth />}>
          <Route path={ROUTES.changePassword} element={<ChangePassword />} />
          <Route element={<BaseLayout />}>
            <Route element={<PageWrapper />}>
              <Route
                path={ROUTES.me}
                element={<Profile />}
                handle={{
                  crumb: () => {
                    return { name: t('common.myProfile'), path: ROUTES.me } as BreadcrumbStep;
                  },
                }}
              />

              {/**** USERS ****/}
              {/* <Route
                path={ROUTES.users}
                handle={{
                  crumb: () => {
                    return { name: t('user.title'), path: ROUTES.users } as BreadcrumbStep;
                  },
                }}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute permissions={['admin']}>
                      <UserList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.userDetail}
                  element={
                    <ProtectedRoute permissions={['admin']}>
                      <UserDetail />
                    </ProtectedRoute>
                  }
                  loader={({ params }) => {
                    return { name: params.userName, path: params.userName } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
              </Route> */}
              {/**** MEDICAL CENTER ****/}
              <Route
                path={ROUTES.medicalCenters}
                handle={{
                  crumb: () => {
                    return { name: t('medicalCenter.title'), path: ROUTES.medicalCenters } as BreadcrumbStep;
                  },
                }}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute permissions={['admin']}>
                      <MedicalCenterList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.new}
                  element={
                    <ProtectedRoute permissions={['admin']}>
                      <MedicalCenterCreate />
                    </ProtectedRoute>
                  }
                  loader={() => {
                    return { name: t('actions.new'), path: ROUTES.new } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
                <Route
                  path={ROUTES.detail}
                  element={
                    <ProtectedRoute permissions={['admin']}>
                      <MedicalCenterDetail />
                    </ProtectedRoute>
                  }
                  loader={({ params }) => {
                    return { name: t('common.detail'), path: params.id } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
              </Route>
              {/**** DOCTOR ****/}
              <Route
                path={ROUTES.doctors}
                handle={{
                  crumb: () => {
                    return { name: t('doctor.title'), path: ROUTES.doctors } as BreadcrumbStep;
                  },
                }}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <DoctorList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.new}
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <DoctorCreate />
                    </ProtectedRoute>
                  }
                  loader={() => {
                    return { name: t('actions.new'), path: ROUTES.new } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
                <Route
                  path={ROUTES.detail}
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <DoctorDetail />
                    </ProtectedRoute>
                  }
                  loader={({ params }) => {
                    return { name: t('common.detail'), path: params.id } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
              </Route>
              {/**** PATIENT ****/}
              <Route
                path={ROUTES.patients}
                handle={{
                  crumb: () => {
                    return { name: t('patient.title'), path: ROUTES.patients } as BreadcrumbStep;
                  },
                }}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <PatientList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.new}
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <PatientCreate />
                    </ProtectedRoute>
                  }
                  loader={() => {
                    return { name: t('actions.new'), path: ROUTES.new } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
                <Route
                  path={ROUTES.detail}
                  loader={({ params }) => {
                    return { name: t('common.detail'), path: params.id } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                >
                  <Route
                    index
                    element={
                      <ProtectedRoute permissions={['medical_center']}>
                        <PatientDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.reportDetail}
                    element={
                      <ProtectedRoute permissions={['medical_center']}>
                        <ReportDetail />
                      </ProtectedRoute>
                    }
                  />
                </Route>
              </Route>
              {/**** REPORT ****/}
              <Route
                path={ROUTES.reports}
                handle={{
                  crumb: () => {
                    return { name: t('report.title'), path: ROUTES.reports } as BreadcrumbStep;
                  },
                }}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <ReportList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.new}
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <ReportCreate />
                    </ProtectedRoute>
                  }
                  loader={() => {
                    return { name: t('actions.new'), path: ROUTES.new } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
                <Route
                  path={ROUTES.reportDetail}
                  element={
                    <ProtectedRoute permissions={['medical_center']}>
                      <ReportDetail />
                    </ProtectedRoute>
                  }
                  loader={({ params }) => {
                    return { name: t('common.detail'), path: params.id } as BreadcrumbStep;
                  }}
                  handle={{
                    crumb: (ret: BreadcrumbStep) => {
                      return ret;
                    },
                  }}
                />
              </Route>
              {/**** PDF CONFIGURATION ****/}
              <Route
                path={ROUTES.pdfConfiguration}
                element={<PdfConfiguration />}
                handle={{
                  crumb: () => {
                    return { name: t('pdfConfig.title'), path: ROUTES.pdfConfiguration } as BreadcrumbStep;
                  },
                }}
              />
            </Route>
          </Route>
        </Route>
      </Route>,
    ),
  );

  return (
    <AuthApiContextProvider>
      <AuthenticationProvider>
        <ApiContextProvider>
          <MessageProvider>
            <DialogProvider>
              <RouterProvider router={router} />
            </DialogProvider>
          </MessageProvider>
        </ApiContextProvider>
      </AuthenticationProvider>
    </AuthApiContextProvider>
  );
};
